import { reactive } from 'vue'
import { Store } from 'vuex'
import { Router } from 'vue-router'
import { State as StoreState } from '@/store/index'
import { ErrorMsgState, useErrorMsg } from '@/model/components/common/message/errorMessage'
import { useNormalMsg } from '@/model/components/common/message/normalMessage'
import { useUserPrelogin, USER_STATUS } from '@/model/api/user/apiUserPreLogin'
import { useStoreAction } from '@/model/store/store'
import { useRouterUtil, RouterTypeData } from '@/model/router/router'
import { useUserAuth } from '@/model/api/user/apiUserAuthinfo'
import * as StateType from '@/store/state'
import * as log from '@/model/util/log/Logger'
import { focusBlur } from '@/model/util/dom/domUtil'
import { State as topState } from './userLoginTopState'

/**
 * 初期DOM構築処理
 * @param state 状態
 * @param store vuex
 * @param router vue router
 * @param auth 認証有無
 */
const initDom = async (state: topState, store: Store<StoreState>, router: Router, auth: boolean) => {
  /**
   * 画面遷移関数
   */
  const redirectRouter = () => {
    // 正常時の場合は画面遷移を実施する
    switch (state.redirect) {
      // ダウンロード画面
      case RouterTypeData.REDIRECT_TYPE.DOWNLOAD: {
        const input: RouterTypeData.DownloadData = {
          downloadKey: state.downloadKey,
        }
        useRouterUtil(router).downlaod(input)
        break
      }
      // パスワード更新画面
      case RouterTypeData.REDIRECT_TYPE.PASSWORD_UPDATE: {
        useRouterUtil(router).passwordUpdate()
        break
      }
      // redirectが設定されていない、または想定外のデータの場合はダウンロード画面に遷移する
      default: {
        const input: RouterTypeData.DownloadData = {}
        useRouterUtil(router).downlaod(input)
        break
      }
    }
  }

  // 非ログイン処理設定
  await useStoreAction(store).init()
  // 認証チェックする場合は、認証情報が取得できる=ログイン済みの場合は情報の取得を実施
  if (auth) {
    // 認証情報取得
    const { normaldata } = await useUserAuth(store).get()
    // 認証が正常の場合は各画面に遷移する
    // 401以外の異常でもエラーメッセージは表示しない
    if (normaldata) {
      // 認証情報設定
      const data: StateType.Login = {
        status: true,
        username: normaldata.username,
        usernamedisp: normaldata.usernamedisp,
        csrftoken: normaldata.csrftoken,
      }
      // 認証情報を保存
      await useStoreAction(store).loginAuthinfo(data)
      // 各画面に遷移する
      redirectRouter()
    }
  }
}

/**
 * TOP画面の初期状態を取得する
 * @param props propsデータ
 * @returns reactiveした初期状態
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useTop = (props: any): topState => {
  const state = reactive<topState>({
    loading: false,
    username: '',
    redirect: props.redirect,
    downloadKey: props.downloadKey,
  })
  return state
}

/**
 * メールアドレス認証を実施して画面遷移を実施する
 * @param state 画面状態
 * @param store vuex
 * @param router vueRouter
 * @param error エラーメッセージ状態
 */
const preauth = async (state: topState, store: Store<StoreState>, router: Router, error: ErrorMsgState) => {
  /**
   * 画面遷移関数
   * @param status ユーザーステータス
   * @param username ユーザー名
   */
  const authRouter = (status: string, username: string) => {
    // 正常時の場合は画面遷移を実施する
    switch (status) {
      // 認証画面に遷移
      case USER_STATUS.ENTRY: {
        const data: RouterTypeData.LoginAuthData = {
          username,
          redirect: state.redirect,
          downloadKey: state.downloadKey,
        }
        useRouterUtil(router).loginAuth(data)
        break
      }
      // ライセンス契約書同意画面に遷移
      case USER_STATUS.PREENTRY: {
        const data: RouterTypeData.AgreeLicenseData = {
          username,
          redirect: state.redirect,
          downloadKey: state.downloadKey,
        }
        useRouterUtil(router).agreeLicense(data)
        break
      }
      default:
      // api関数でチェック済みのため何もしない
    }
  }

  // 一旦メッセージを全て初期化
  error.data.splice(0)
  // モバイルのためにfocusを外す
  focusBlur()
  // ローディングがONの場合は実行中とみなして何もしない
  if (state.loading) {
    log.debug(`Already running. [preauth]`)
    return
  }
  // ローディングをONにする
  state.loading = true
  // api送信を実施
  state.username = store.state.screen.top.username
  const { normaldata } = await useUserPrelogin(store).post(error)
  // ローディングをOFFにする
  state.loading = false
  // レスポンスデータに従い画面遷移を実施する
  if (normaldata) {
    // 正常時の場合は画面遷移を実施する
    const status = normaldata.userstatus
    authRouter(status, state.username)
  }
}

/**
 * パスワードリセット画面遷移を実施する
 * @param router vueRouter
 */
const resetpassword = async (router: Router) => {
  useRouterUtil(router).resetPassword()
}

export { initDom, useTop, preauth, resetpassword, useErrorMsg, useNormalMsg }
